import { createEmpty, extend, getCenter, isEmpty } from 'ol/extent';
import { getResolutionForScale } from './utils';

// Padding standard (px): featura fit-uita nu trebuie sa ramana lipita de marginile hartii.
export const FIT_PADDING = [100, 100, 100, 100];

export const DEFAULT_FIT_DURATION = 1000;

// Doar punctul are nevoie de o scara implicita: extentul lui e degenerat si nu poate decide zoom-ul.
// Pentru linii si poligoane bbox-ul decide, iar limita de zoom-in ramane MinScale-ul view-ului.
export const POINT_FIT_SCALE = 500;

// Layerul se randeaza doar in intervalul lui de scari, deci un fit mai adanc decat MinScale-ul
// layerului centreaza featura pe un layer care nu se mai deseneaza.
function getLayerMinResolution(layer) {
    if (layer == null || typeof layer.get !== 'function') {
        return null;
    }

    let minResolution = layer.get('minResolution');

    return minResolution != null && !isNaN(minResolution) ? minResolution : null;
}

// Limita de zoom-in = cea mai restrictiva dintre MinScale-ul hartii si al layerului tinta.
function clampMinResolution(view, resolution, layer) {
    let limits = [resolution, view.getMinResolution(), getLayerMinResolution(layer)];

    limits = limits.filter(x => x != null && !isNaN(x));

    return limits.length > 0 ? Math.max.apply(null, limits) : resolution;
}

// Padding-ul poate veni ca numar (uniform) sau ca [sus, dreapta, jos, stanga]; null = se ia cel standard.
export function normalizePadding(padding) {
    if (padding == null) {
        return null;
    }

    if (Array.isArray(padding)) {
        return padding.length == 4 ? padding : null;
    }

    return !isNaN(padding) ? [padding, padding, padding, padding] : null;
}

// Reuniunea extentelor; null cand nu exista nicio geometrie, ca sa nu ajunga un extent gol la fit().
export function getFeaturesExtent(features) {
    let extent = createEmpty();

    for (let i = 0; i < features.length; i++) {
        if (features[i] == null) {
            continue;
        }

        let geom = features[i].getGeometry();

        if (geom != null && geom.getExtent() != null) {
            extend(extent, geom.getExtent());
        }
    }

    return isEmpty(extent) ? null : extent;
}

/**
 * Fit unic pentru toata harta: acelasi padding, aceleasi garzi si aceeasi interpretare a scarii.
 *
 * @param {import('ol/View').default} view
 * @param {number[]} extent
 * @param {{ projCode?: string, scale?: number, minScale?: number, minResolution?: number, layer?: object, duration?: number, padding?: number[] }} [options]
 *        layer = layerul tinta; fit-ul nu coboara sub MinScale-ul lui, ca sa nu ramana nedesenat.
 *        scale = zoom exact cerut de apelant; minScale/minResolution = doar limita de zoom-in.
 * @returns {boolean} false cand nu exista nimic de fit-uit.
 */
export function fitExtent(view, extent, options) {
    if (view == null || extent == null || isEmpty(extent)) {
        return false;
    }

    options = options || {};

    let projCode = options.projCode != null ? options.projCode : view.getProjection().getCode();
    let duration = options.duration != null ? options.duration : DEFAULT_FIT_DURATION;
    let center = getCenter(extent);

    // Scara ceruta explicit se aplica exact, nu ca limita.
    if (options.scale != null && !isNaN(options.scale) && options.scale >= 0) {
        view.animate({
            center: center,
            resolution: clampMinResolution(view, getResolutionForScale(options.scale, projCode), options.layer),
            duration: duration
        });

        return true;
    }

    let requestedMinResolution = options.minResolution != null ? options.minResolution : getResolutionForScale(options.minScale, projCode);

    // Extent degenerat (punct): fit() ar cobori pana la rezolutia minima a view-ului, deci centram noi.
    if (extent[0] == extent[2] && extent[1] == extent[3]) {
        let pointResolution = requestedMinResolution != null ? requestedMinResolution : getResolutionForScale(POINT_FIT_SCALE, projCode);

        view.animate({
            center: center,
            resolution: clampMinResolution(view, pointResolution, options.layer),
            duration: duration
        });

        return true;
    }

    let padding = normalizePadding(options.padding);

    view.fit(extent, {
        duration: duration,
        padding: padding != null ? padding : FIT_PADDING,
        minResolution: clampMinResolution(view, requestedMinResolution != null ? requestedMinResolution : 0, options.layer)
    });

    return true;
}

/**
 * Fit pe una sau mai multe feature-uri, pe extentul lor reunit.
 *
 * @param {import('ol/View').default} view
 * @param {import('ol/Feature').default|import('ol/Feature').default[]} features
 * @param {{ projCode?: string, scale?: number, minScale?: number, minResolution?: number, layer?: object, duration?: number, padding?: number[] }} [options]
 *        layer = layerul tinta; fit-ul nu coboara sub MinScale-ul lui, ca sa nu ramana nedesenat.
 * @returns {boolean}
 */
export function fitFeatures(view, features, options) {
    let list = Array.isArray(features) ? features : (features != null ? [features] : []);

    return fitExtent(view, getFeaturesExtent(list), options);
}
